import { MandateState, Status } from '@smg-real-estate/seller-leads-schema';
import isEqual from 'lodash.isequal';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';

import { getLeadById, getLeads, INITIAL_PAGE } from '@/api/sellerLeads';
import { getQueryParams } from '@/helpers/SellerLeadsUtils/sellerLeadsUtils';
import {
  LeadSource,
  LeadSourceToOriginSource,
  QualifiedOptions,
  Sort,
  type DisplayableLead,
  type LeadsResponseBody,
  type SearchFilters,
  type SelectedStatus,
} from '@/models/sellerLead';
import { type InsertionFunnelState } from '@/store/types/insertion-funnel-store';
import { adaptData, updateRefundProperties } from '@/views/SellerLeads/Details/adaptData';

export interface Filters {
  searchText: string;
  status: Status[];
  mandateState: MandateState[];
  source: LeadSource[];
  sortOrder: Sort;
  page: number;
  qualified: QualifiedOptions[];
}

export interface SellerLeadsState {
  filters: Filters;
  selectedLead: DisplayableLead | null;
  selectedStatus: SelectedStatus;
  hasStatusError: boolean;
  detailPgNotFound: boolean;
  detailPgLoading: boolean;
  agencyLeadsResponse: LeadsResponseBody | null;
  activeDistrictsSaved: boolean;
}

export interface Getters {
  getFilters: (state: SellerLeadsState) => Filters;
  getSelectedLead: (state: SellerLeadsState) => DisplayableLead | null;
  getSelectedStatus: (state: SellerLeadsState) => SelectedStatus;
  hasFilters: (state: SellerLeadsState) => boolean;
  getSearchFilters: (state: SellerLeadsState) => SearchFilters;
  getSearchFiltersForUrl: (state: SellerLeadsState, getters: Getters) => string;
  getHasStatusError: (state: SellerLeadsState) => boolean;
  getIsLoading: (state: SellerLeadsState) => boolean;
  getDetailPgNotFound: (state: SellerLeadsState) => boolean;
  getHasAgencyLeads: (state: SellerLeadsState) => boolean;
  getActiveDistrictsSaved: (state: SellerLeadsState) => boolean;
}

interface Mutations {
  setFilters: (state: SellerLeadsState, filters: Filters) => void;
  setSelectedLead: (state: SellerLeadsState, lead: DisplayableLead | null) => void;
  setSelectedStatus: (state: SellerLeadsState, selectedStatus: SelectedStatus) => void;
  setHasStatusError: (state: SellerLeadsState, hasStatusError: boolean) => void;
  checkSelectedLead: (state: SellerLeadsState, leadId: string) => void;
  setDetailPageError: (state: SellerLeadsState, detailPgNotFound: boolean) => void;
  setAgencyLeadsResponse: (state: SellerLeadsState, response: LeadsResponseBody | null) => void;
  setActiveDistrictsSaved: (state: SellerLeadsState, activeDistrictsSaved: boolean) => void;
}

export const getInitialState = (): SellerLeadsState => ({
  filters: {
    searchText: '',
    status: Object.values(Status),
    source: Object.values(LeadSource),
    sortOrder: Sort.Desc,
    page: INITIAL_PAGE,
    qualified: Object.values(QualifiedOptions),
    mandateState: Object.values(MandateState),
  },
  selectedLead: null,
  selectedStatus: {
    status: Status.NewLeadReceived,
    mandateState: null,
  },
  hasStatusError: false,
  detailPgNotFound: false,
  detailPgLoading: false,
  agencyLeadsResponse: null,
  activeDistrictsSaved: false,
});

export const getDefaultFilters = (): Filters => {
  const queryParams = getQueryParams();

  return {
    searchText: queryParams.searchText,
    status: queryParams.status,
    source: queryParams.source,
    sortOrder: queryParams.sortOrder,
    page: queryParams.page,
    qualified: queryParams.qualified,
    mandateState: queryParams.mandateState,
  };
};

const getLeadDetailsById = async (state: SellerLeadsState, leadId: string) => {
  const { statusCode, results } = (await getLeadById(leadId)) as {
    statusCode: number;
    results: any;
  };
  if (statusCode == 200 && !!results) {
    mutations.setSelectedLead(state, adaptData(results));
  } else if (statusCode == 404 || !results) {
    mutations.setDetailPageError(state, true);
  } else {
    mutations.setDetailPageError(state, false);
  }
};

const getters: GetterTree<SellerLeadsState, InsertionFunnelState> & Getters = {
  getFilters: (state: SellerLeadsState): Filters => state.filters,
  getSelectedLead: (state: SellerLeadsState): DisplayableLead | null => state.selectedLead,
  getSelectedStatus: (state: SellerLeadsState): SelectedStatus => state.selectedStatus,
  hasFilters: (state: SellerLeadsState): boolean =>
    !isEqual(state.filters, getInitialState().filters),
  getSearchFilters: (state: SellerLeadsState): SearchFilters => {
    const isSelectedAllStatus = Object.values(Status).length === state.filters.status.length;
    const isSelectedAllSource = Object.values(LeadSource).length === state.filters.source.length;
    const isSelectedAllQualification =
      Object.values(QualifiedOptions).length === state.filters.qualified.length;
    const isSelectedNoneQualification = state.filters.qualified.length === 0;
    const isSelectedNoneStatus = state.filters.status.length === 0;
    const isSelectedNoneSource = state.filters.source.length === 0;

    const isSelectedNoneMandateState = state.filters.mandateState.length === 0;
    const isSelectedAllMandateState =
      Object.values(MandateState).length === state.filters.mandateState.length;
    return {
      searchText: state.filters.searchText || undefined,
      status: isSelectedAllStatus || isSelectedNoneStatus ? undefined : state.filters.status,
      source:
        isSelectedAllSource || isSelectedNoneSource
          ? undefined
          : state.filters.source.map((item) => LeadSourceToOriginSource[item]).flat(),
      sortOrder: state.filters.sortOrder,
      qualified:
        isSelectedAllQualification || isSelectedNoneQualification
          ? undefined
          : state.filters.qualified,
      mandateState:
        isSelectedAllMandateState || isSelectedNoneMandateState
          ? undefined
          : state.filters.mandateState,
    };
  },
  getSearchFiltersForUrl: (_state: SellerLeadsState, localGetters: Getters): string => {
    const searchFilters = localGetters.getSearchFilters;
    return `${encodeURIComponent(JSON.stringify(searchFilters))}`;
  },
  getHasStatusError: (state: SellerLeadsState): boolean => state.hasStatusError,
  getIsLoading: (state: SellerLeadsState): boolean => state.detailPgLoading,
  getDetailPgNotFound: (state: SellerLeadsState): boolean => state.detailPgNotFound,
  getHasAgencyLeads: (state: SellerLeadsState): boolean =>
    (state.agencyLeadsResponse?.totalItems ?? 0) > 0,
  getActiveDistrictsSaved: (state: SellerLeadsState): boolean => state.activeDistrictsSaved,
};

const mutations: MutationTree<SellerLeadsState> & Mutations = {
  setFilters: (state: SellerLeadsState, filters: Filters): void => {
    state.filters = filters;
  },
  setSelectedLead(state: SellerLeadsState, lead: DisplayableLead | null): void {
    state.selectedLead = lead ? updateRefundProperties(lead) : null;
    state.selectedStatus = {
      status: lead?.status ?? Status.NewLeadReceived,
      mandateState: lead?.mandateState ?? null,
    };
    state.detailPgLoading = false;
  },
  setSelectedStatus(state: SellerLeadsState, selectedStatus: SelectedStatus): void {
    state.selectedStatus = selectedStatus;
  },
  setHasStatusError(state: SellerLeadsState, hasStatusError: boolean): void {
    state.hasStatusError = hasStatusError;
  },
  checkSelectedLead(state: SellerLeadsState, leadId: string): void {
    state.detailPgNotFound = false;
    if (!state.selectedLead || state.selectedLead.leadId != leadId) {
      state.detailPgLoading = true;
      getLeadDetailsById(state, leadId);
    }
  },
  setDetailPageError(state: SellerLeadsState, detailPgNotFound: boolean): void {
    state.detailPgNotFound = detailPgNotFound;
    state.detailPgLoading = false;
  },
  setAgencyLeadsResponse(state: SellerLeadsState, response: LeadsResponseBody | null): void {
    state.agencyLeadsResponse = response;
  },
  setActiveDistrictsSaved(state: SellerLeadsState, activeDistrictsSaved: boolean): void {
    state.activeDistrictsSaved = activeDistrictsSaved;
  },
};

const actions: ActionTree<SellerLeadsState, InsertionFunnelState> = {
  async fetchAgencyLeads({ commit, rootGetters }): Promise<void> {
    const activeAgencyId = rootGetters.getActiveAgencyId;
    if (!activeAgencyId) {
      commit('setAgencyLeadsResponse', null);
      return;
    }

    const response = await getLeads(INITIAL_PAGE, { sortOrder: Sort.Desc });
    commit('setAgencyLeadsResponse', response);
  },
};

const leadStore = {
  state: { ...getInitialState(), filters: getDefaultFilters() },
  getters,
  mutations,
  actions,
};
export default leadStore;
